import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '@/api/backend';
import { AuthState, useAuthStore, useIsLoggedIn } from '@/api/login';
import i18n from '@/i18n';
import { Button } from '@/primitives/Button';
import { getSourceConfig } from '@/theme';

import styles from './ResellerLogin.module.css';
const ENVIRONMENT = import.meta.env.VITE_CUTR_ENV;

export const ResellerLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isLoggedIn = useIsLoggedIn();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>();
  const { login } = useAuthStore();
  const config = getSourceConfig();

  const tokenParam = searchParams.get('token');

  useEffect(() => {
    const handleCall = async () => {
      if (!tokenParam) return;
      try {
        const data = await api.loginByKey(tokenParam);
        login(data as AuthState);
      } catch (err) {
        setError(String(err));
      }
    };
    handleCall();
  }, [tokenParam, login]);

  if (isLoggedIn) {
    const redirectUrl = decodeURIComponent(
      searchParams.get('redirect') || '/windows/workspace'
    );
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('redirect');
    newParams.delete('token');
    const paramString = newParams.toString();
    const to = paramString ? `${redirectUrl}?${paramString}` : redirectUrl;
    return <Navigate to={to} replace />;
  }

  if (tokenParam) {
    // todo: show spinner?
    return null;
  }

  const loginUrl = config.loginUrl?.(i18n.resolvedLanguage || 'en');

  const handleDevLogin = () => {
    api
      .devLogin()
      .then((data) => {
        const { email, token, userLeadId } = data as AuthState;
        login({ email, token, clientNumber: null, userLeadId });
        const redirectUrl = decodeURIComponent(
          searchParams.get('redirect') || '/windows/workspace'
        );
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('redirect');
        const paramString = newParams.toString();
        navigate(paramString ? `${redirectUrl}?${paramString}` : redirectUrl);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  return (
    <main className="content">
      <div className={styles.resellerLogin}>
        <div className={styles.header}>
          <h2>{t('reseller.login.title')}</h2>
          <p>{t('reseller.login.description')}</p>
        </div>
        {error && <p className="error">{error}</p>}
        {ENVIRONMENT !== 'production' && (
          <Button onClick={handleDevLogin}>{'Dev login'}</Button>
        )}
        {loginUrl && (
          <Button href={loginUrl} as="a">
            {t('reseller.login.cta')}
          </Button>
        )}
      </div>
    </main>
  );
};
